import { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { normalizeNeighborhood } from "@/lib/bairro";

/**
 * O bairro da entrega por bairro no checkout (Pedro, 30/10/2026: «campo de pesquisa na lista de bairros… várias lojas
 * têm lista muito grande»). Até BAIRROS_PARA_PESQUISA − 1 bairros fica o select do sistema, como sempre; a partir daí o
 * campo abre um painel com a pesquisa, que compara como a taxa (sem acento, minúsculas, sem espaço nas pontas). A lista
 * é a do lojista, em ordem alfabética e sem preço; o valor é o `name` dela (ou "__other__" para «Outro bairro»), o
 * mesmo que o select mandava. O painel ocupa só a área visível da tela (visualViewport), para o teclado do celular não
 * esconder a lista, e o «Outro bairro» fica fixo embaixo: dá para chegar nele mesmo quando a pesquisa não acha nada.
 */
export const BAIRROS_PARA_PESQUISA = 8;
export const OUTRO_BAIRRO = "__other__";

interface Props {
  id?: string;
  value: string;
  bairros: { name: string }[];
  aceitaOutro: boolean;
  placeholder: string;
  className: string;
  testId?: string;
  onChange: (valor: string) => void;
}

export function SeletorDeBairro(props: Props) {
  if (props.bairros.length < BAIRROS_PARA_PESQUISA) {
    const { id, value, bairros, aceitaOutro, placeholder, className, testId, onChange } = props;
    return (
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={className} data-testid={testId}>
        <option value="">{placeholder}</option>
        {bairros.map((n) => (
          <option key={n.name} value={n.name}>
            {n.name}
          </option>
        ))}
        {aceitaOutro && <option value={OUTRO_BAIRRO}>Outro bairro</option>}
      </select>
    );
  }
  return <SeletorComPesquisa {...props} />;
}

// A caixa do painel na área VISÍVEL da tela: com o teclado aberto, o celular encolhe o visualViewport e o painel
// encolhe junto (a lista termina em cima do teclado, no iPhone e no Android).
function useCaixaVisivel(aberto: boolean) {
  const ler = () => {
    const vv = window.visualViewport;
    const w = vv?.width ?? window.innerWidth;
    const h = vv?.height ?? window.innerHeight;
    const x = vv?.offsetLeft ?? 0;
    const y = vv?.offsetTop ?? 0;
    if (w < 640) return { left: x + 12, top: y + 12, width: w - 24, height: h - 24 };
    const largura = Math.min(440, w - 32);
    const altura = Math.min(560, h - 64);
    return { left: x + (w - largura) / 2, top: y + (h - altura) / 2, width: largura, height: altura };
  };
  const [caixa, setCaixa] = useState(ler);
  useEffect(() => {
    if (!aberto) return;
    const vv = window.visualViewport;
    const atualiza = () => setCaixa(ler());
    atualiza();
    vv?.addEventListener("resize", atualiza);
    vv?.addEventListener("scroll", atualiza);
    window.addEventListener("resize", atualiza);
    return () => {
      vv?.removeEventListener("resize", atualiza);
      vv?.removeEventListener("scroll", atualiza);
      window.removeEventListener("resize", atualiza);
    };
  }, [aberto]);
  return caixa;
}

function SeletorComPesquisa({ id, value, bairros, aceitaOutro, placeholder, className, testId, onChange }: Props) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const pesquisa = useRef<HTMLInputElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const caixa = useCaixaVisivel(aberto);
  const termo = normalizeNeighborhood(busca);
  const achados = useMemo(() => (termo ? bairros.filter((b) => normalizeNeighborhood(b.name).includes(termo)) : bairros), [bairros, termo]);
  const rotulo = value === OUTRO_BAIRRO ? "Outro bairro" : value;

  const escolher = (v: string) => {
    onChange(v);
    setAberto(false);
    setBusca("");
  };

  // Ao abrir com um bairro escolhido, a lista já mostra ele.
  useEffect(() => {
    if (!aberto || !value || value === OUTRO_BAIRRO) return;
    const t = setTimeout(() => document.querySelector('[data-bairro-escolhido="1"]')?.scrollIntoView({ block: "center" }), 60);
    return () => clearTimeout(t);
  }, [aberto, value]);

  return (
    <>
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={aberto}
        aria-haspopup="dialog"
        onClick={() => setAberto(true)}
        className={`${className} items-center justify-between gap-2 text-left`}
        data-testid={testId}
      >
        <span className={`min-w-0 truncate ${rotulo ? "" : "text-muted-foreground"}`}>{rotulo || placeholder}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
      </button>
      <DialogPrimitive.Root
        open={aberto}
        onOpenChange={(o) => {
          setAberto(o);
          if (!o) setBusca("");
        }}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[10020] bg-black/50" />
          <DialogPrimitive.Content
            ref={painel}
            style={caixa}
            className="fixed z-[10021] flex flex-col overflow-hidden rounded-xl border bg-background shadow-xl focus:outline-none"
            aria-describedby={undefined}
            // No computador a pesquisa já vem em foco. No celular o painel abre mostrando a lista (o teclado só sobe
            // quando o cliente toca na pesquisa; o iPhone nem abriria o teclado num foco fora do toque).
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              if (window.matchMedia("(pointer: coarse)").matches) painel.current?.focus();
              else pesquisa.current?.focus();
            }}
            data-testid="bairro-painel"
          >
            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
              <DialogPrimitive.Title className="text-base font-semibold">Escolha o bairro</DialogPrimitive.Title>
              <DialogPrimitive.Close className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Fechar">
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>
            <div className="border-b p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input
                  ref={pesquisa}
                  type="search"
                  inputMode="search"
                  enterKeyHint="search"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter com um bairro só na lista escolhe ele.
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (achados.length === 1) escolher(achados[0]!.name);
                    }
                  }}
                  placeholder="Pesquisar bairro"
                  aria-label="Pesquisar bairro"
                  className="h-11 w-full rounded-lg border border-input bg-muted/30 pl-9 pr-10 text-base outline-none focus:ring-2 focus:ring-ring [&::-webkit-search-cancel-button]:appearance-none"
                  data-testid="bairro-pesquisa"
                />
                {busca && (
                  <button
                    type="button"
                    onClick={() => {
                      setBusca("");
                      pesquisa.current?.focus();
                    }}
                    className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                    aria-label="Apagar pesquisa"
                    data-testid="bairro-apagar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" role="listbox" aria-label="Bairros" data-testid="bairro-lista">
              {achados.map((b) => {
                const escolhido = b.name === value;
                return (
                  <button
                    key={b.name}
                    type="button"
                    role="option"
                    aria-selected={escolhido}
                    onClick={() => escolher(b.name)}
                    className={`flex w-full items-center justify-between gap-3 border-b px-4 py-3 text-left text-sm hover:bg-muted ${escolhido ? "bg-primary/5 font-semibold" : ""}`}
                    data-bairro-escolhido={escolhido ? "1" : undefined}
                    data-testid="bairro-opcao"
                  >
                    <span className="min-w-0 break-words">{b.name}</span>
                    {escolhido && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />}
                  </button>
                );
              })}
              {achados.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground" data-testid="bairro-nenhum">
                  Nenhum bairro com «{busca.trim()}».
                  {aceitaOutro ? " Se o seu não está na lista, escolha «Outro bairro»." : " Confira o nome ou apague a pesquisa para ver todos."}
                </p>
              )}
            </div>
            {aceitaOutro && (
              <div className="border-t p-3">
                <button
                  type="button"
                  onClick={() => escolher(OUTRO_BAIRRO)}
                  className={`flex h-11 w-full items-center justify-center rounded-lg border text-sm font-medium hover:bg-muted ${value === OUTRO_BAIRRO ? "border-primary bg-primary/5" : ""}`}
                  data-testid="bairro-outro"
                >
                  Outro bairro
                </button>
              </div>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
